import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Template } from "@/types/template";
import { cn, nonFalse, str } from "@/lib/utils";
import { useState } from "react";

import { Stepper } from "@/components/Stepper";
import { LaunchHeader } from "@/components/launch/LaunchHeader";
import { ProviderType, ProviderTypes } from "@/types";
import useSWR from "swr";
import { getProviderMeta } from "@/lib/provider/meta";
import { useRouter } from "next/router";
import { DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

export const ChooseHostingOption = ({
  template,
  onNext
}: {
  template: Template;
  onNext?: (choice: "provision" | "existing", provider: ProviderType) => void;
}) => {
  const [choice, setChoice] = useState<"provision" | "existing" | null>(null);
  const { data: configured } = useSWR<ProviderType[]>("/api/tenant/provider");
  const router = useRouter();
  const provider = (str(router.query.provider) ??
    (configured?.includes("do")
      ? "do"
      : configured && configured.length > 0
        ? configured[0]
        : "do")) as ProviderType;

  if (!ProviderTypes.includes(provider)) {
    throw new Error(`Invalid provider: ${provider}`);
  }

  const handleChooseProvider = (provider: ProviderType) => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        provider
      }
    });
  };

  const meta = getProviderMeta(provider);

  return (
    <div className="flex flex-col justify-center items-center w-full min-h-screen p-4">
      <LaunchHeader
        name={template?.name}
        logo={template?.logo}
        subtitle="Choose Your Hosting Option"
        className="mb-6"
      />
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <Card
          className={cn(
            "w-full md:w-80 cursor-pointer transition-all hover:scale-105 hover:border-accent-foreground hover:bg-muted",
            choice === "provision" ? "border-1 border-accent-foreground/60" : ""
          )}
          onClick={() => setChoice("provision")}
        >
          <CardHeader>
            <CardTitle>Create VPS automatically</CardTitle>
            <CardDescription>Set up a new server</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside text-sm">
              <li>Easy setup process</li>
              <li>Optimized for DollarDeploy</li>
              <li>Quick provisioning</li>
            </ul>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "w-full md:w-80 cursor-pointer transition-all hover:scale-105 hover:border-accent-foreground hover:bg-muted",
            choice === "existing" ? "border-1 border-accent-foreground/60" : ""
          )}
          onClick={() => {
            setChoice("existing");
          }}
        >
          <CardHeader>
            <CardTitle>Bring Your Own Server</CardTitle>
            <CardDescription>Use an existing server you own</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside text-sm">
              <li>Use your preferred provider</li>
              <li>Reuse existing infrastructure</li>
              <li>More control over setup</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {choice !== "provision" || configured?.length === 0 ? (
        <Button
          size="lg"
          disabled={!choice}
          onClick={() => choice && onNext && onNext(choice, provider)}
        >
          {choice === "existing" ? "Use existing server" : "Next"}
        </Button>
      ) : (
        <div className="flex items-center">
          <Button
            size="lg"
            variant="default"
            className="rounded-r-none h-10 flex items-center gap-2"
            onClick={() => choice && onNext && onNext(choice, provider)}
            disabled={!provider}
          >
            <img
              src={meta?.logoUrl}
              className="h-7 w-7 inline-block bg-white border-2 rounded-md"
              alt={meta?.name}
              title={meta?.name}
            />
            Use {meta?.name}
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                className="rounded-l-none border-l border-l-slate-400 px-2 h-10"
                size="sm"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {ProviderTypes?.map(getProviderMeta)
                .filter(nonFalse)
                .filter(p => p.compute)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(provider => (
                  <DropdownMenuItem
                    key={provider.id}
                    onClick={() => {
                      handleChooseProvider(provider.id);
                    }}
                    className="flex gap-1"
                  >
                    Select {provider.name}
                    {provider.experimental ? (
                      <span className="text-xs text-muted-foreground">(experimental)</span>
                    ) : null}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="mt-8">
        <Stepper steps={3} current={0} />
      </div>
    </div>
  );
};
